import { z } from "zod";

/**
 * editProfileSchema (player Me slice).
 *
 * Mirrors the REAL backend contract `UserProfileUpdate` (common/models.py:1205;
 * the generated `@bw/shared` alias) — a TRUE partial PATCH — and enforces ONLY
 * the same constraints the backend enforces. It invents NO extra policy
 * (binding: zero business logic in the frontend).
 *
 *   EDITABLE this slice (subset of UserProfileUpdate):
 *     - display_name : non-empty (trimmed)
 *     - gender       : ProfileGender — only while unset (a legacy profile may set it
 *                      once; the server rejects a change after that)
 *     - play_formats / skill_level : how they play (shown on the profile)
 *     - avatar       : "" or an https:// URL (the backend validator requires https://)
 *     - bio          : free text (optional)
 *     - visibility   : public | private (the only two stored values; the form narrows here)
 *     - profile_shows_play_details : opt-in to list home area, places and
 *                      communities publicly (the server honours it only for adults)
 *
 *   EXCLUDED (deliberate, per plan):
 *     - date_of_birth: COPPA PII — NOT returned on UserProfileResponse, cannot
 *                      prefill, and re-submitting re-runs the server age gate
 *                      (_compute_age_status → account_status flip). It must NEVER
 *                      be a key this schema accepts.
 *     - user_id / account_status / created_at / is_minor / requires_parental_consent:
 *                      immutable / derived — never editable.
 *
 * `.strip()` (the Zod object default) drops unknown keys, so an invented client
 * field — or a smuggled `date_of_birth` / immutable key — can never reach the
 * backend.
 */

// The four real ProfileGender enum values — nothing invented. Copied (not
// cross-imported) from the onboarding pattern to preserve module isolation.
export const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
] as const;

// visibility narrows to the only two stored values (per the demo settings screen
// + the UserProfileORM doc). The backend column is a free-form `str` with no
// server enum yet — see the drift note in the README / plan (a backend
// `Literal['public','private']` hardening is a tracked follow-up, not this slice).
export const VISIBILITY_OPTIONS = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
] as const;

function isUrl(s: string): boolean {
  try {
    // eslint-disable-next-line no-new
    new URL(s);
    return true;
  } catch {
    return false;
  }
}

const displayName = z.string().trim().min(1, "Enter a display name.");

const gender = z.enum(["male", "female", "other", "prefer_not_to_say"]);

// Optional avatar. An EMPTY string (an untouched <input>) is treated as
// "omitted" and passes; the https rule only applies once the user enters a
// value (mirrors the backend avatar validator: must start "https://"). Keeping
// the field a plain `string | undefined` (no z.preprocess) keeps the schema's
// input and output types aligned so RHF's resolver types cleanly.
const avatar = z
  .string()
  .refine((s) => s === "" || (isUrl(s) && s.startsWith("https://")), {
    message: "Avatar URL must start with https://.",
  })
  .optional();

// Optional bio — any free text, including empty.
const bio = z.string().optional();

const visibility = z.enum(["public", "private"]);

/**
 * editProfileSchema — only the real editable `UserProfileUpdate` keys for
 * this slice. `.strip()` (the Zod default for objects) drops any unknown key, so
 * `date_of_birth`, immutable/derived fields, and invented client fields can never
 * reach the PATCH body.
 */
export const editProfileSchema = z.object({
  display_name: displayName,
  gender: gender.optional(),
  play_formats: z.array(z.enum(["singles", "doubles", "mixed"])).optional(),
  skill_level: z.enum(["beginner", "intermediate", "advanced"]).optional(),
  avatar,
  bio,
  visibility: visibility.optional(),
  profile_shows_play_details: z.boolean().optional(),
});

export type EditProfileValues = z.infer<typeof editProfileSchema>;
